import React, { useEffect, useRef } from 'react';
import { ISideNavRoute } from '../side-nav.models';
import { useLocation, useNavigate } from 'react-router-dom';
import { SideNavDialogRouteList } from './side-nav-dialog-route-list/side-nav-dialog-route-list';
import { AvatarIcon, CloseButton } from '@keplerco/core';
import navStyles from '../../navigation.module.css';
import sideNavDialogStyles from './side-nav-dialog.module.css';
import classNames from 'classnames';

function isInsideClick(element: HTMLDialogElement | null, event: React.MouseEvent<HTMLDialogElement, MouseEvent>): boolean {
  if (!element) return false;

  const content = element.getBoundingClientRect();
  const verticalInside = content.top <= event.clientY && content.bottom >= event.clientY;
  const horizontalInside = content.left <= event.clientX && content.right >= event.clientX;
  return verticalInside && horizontalInside;
}

export function SideNavDialog(props: { learnerRoutes: ISideNavRoute[]; companyRoutes: ISideNavRoute[]; open: boolean; onClose: () => void; onOpenSettings: () => void }): JSX.Element {
  const { learnerRoutes, companyRoutes, open, onClose, onOpenSettings } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    function handleTouchStartEvent(touchStartEvent: TouchEvent) {
      if (!dialog) return;

      const startX = touchStartEvent.touches[0].clientX;
      const paneWidth = dialog.getBoundingClientRect().width;
      let activateSwipe = false;
      let totalMovement = 0;
      let totalMovementPercent = 0;

      function handleTouchMove(touchMoveEvent: TouchEvent) {
        if (!dialog) return;

        const moveX = touchMoveEvent.touches[0].clientX;
        totalMovement = moveX - startX;
        totalMovementPercent = totalMovement / paneWidth;

        activateSwipe = totalMovement >= 10;

        if (activateSwipe) {
          if (closeButtonRef.current) {
            closeButtonRef.current.style.transitionDuration = `0ms`;
            closeButtonRef.current.style.transform = `translateX(${Math.min(1, totalMovementPercent) * 200}%)`;
            closeButtonRef.current.style.opacity = `${1 - totalMovementPercent}`;
          }

          dialog.style.transitionDuration = '0ms';
          dialog.style.transform = `translateX(${Math.max(totalMovement, 0)}px)`;
        }
      }

      window.addEventListener('touchmove', handleTouchMove);

      function handleTouchEnd(touchEndEvent: TouchEvent) {
        if (!dialog) return;

        if (activateSwipe) {
          touchEndEvent.preventDefault();
          touchEndEvent.stopPropagation();
        }

        window.removeEventListener('touchmove', handleTouchMove);
        dialog.style.transitionDuration = '';
        if (closeButtonRef.current) closeButtonRef.current.style.transitionDuration = '';

        if (totalMovementPercent >= 0.45) {
          dialog.close();
          onClose();
        }

        dialog.style.transform = ``;
        if (closeButtonRef.current) {
          closeButtonRef.current.style.transform = ``;
          closeButtonRef.current.style.opacity = ``;
        }
      }

      window.addEventListener('touchend', handleTouchEnd, { once: true });
    }

    if (open) {
      dialog.addEventListener('touchstart', handleTouchStartEvent);
      dialog.showModal();
      return;
    }

    return () => dialog.removeEventListener('touchstart', handleTouchStartEvent);
  }, [open]);


  function closeDialog() {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // dialog.classList.add('close'); // run animation here
    dialog.classList.add(sideNavDialogStyles.close); // run animation here
    onClose();

    dialog.addEventListener(
      'animationend',
      () => {
        // dialog.classList.remove('close');
        dialog.classList.remove(sideNavDialogStyles.close);
        dialog.close(); // then run the default close method
      },
      { once: true }
    ); // add this to prevent bugs when reopening the modal
  }

  return (
    <dialog
      ref={dialogRef}
      className={sideNavDialogStyles.sideNavDialogContent}
      onClick={event => {
        if (!isInsideClick(dialogRef.current, event)) closeDialog();
      }}
    >
      <CloseButton onClick={closeDialog} />

      <header className={sideNavDialogStyles.sideNavDialogHeader}>
        <div className={classNames(navStyles.navAvatarIconContainer, sideNavDialogStyles.sideNavDialogAvatarIconContainer)}>
          <AvatarIcon />
        </div>
      </header>


      <React.Fragment>
        <SideNavDialogRouteList sideNavParentRoutes={learnerRoutes} pathname={pathname} onClick={closeDialog} />
      </React.Fragment>

      <hr className={navStyles.navDivider} />
      <a
        className={classNames(navStyles.navCaption, sideNavDialogStyles.sideNavDialogYourAccount)}
        href="https://legal.keplerco.io"
        target="_blank"
        rel="noopener noreferrer"
        onClick={closeDialog}
      >
        <img className={navStyles.navIcon} src="/icons/ico_legal_tile.svg" alt="Legal Icon" />
        Legal
      </a>

      <button
        className={classNames(navStyles.navCaption, sideNavDialogStyles.sideNavDialogYourAccount)}
        onClick={async () => {
        }}
      >
        <img className={navStyles.navIcon} src="/icons/ico_logout_tile.svg" alt="Sign Out Icon" />
        Log Out
      </button>
    </dialog>
  );
}

